<template>
    <Head :title="__('Edit')"/>

    <BreezeDashboardLayout>
        <template #header>
            <div class="font-semibold text-xl text-gray-800 leading-tight">
                {{ __('Edit') }}
            </div>
        </template>

        <notification v-if="success" color="success">
            {{ success }}
        </notification>

        <notification v-if="Object.keys(errors).length" color="danger">
            <ul class="list-inside">
                <li v-for="error in errors">{{ error }}</li>
            </ul>
        </notification>

        <the-modal-box v-model="isConfirmModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmDeletion">
            <p>{{ __('Are you sure you want to delete this resource?') }}</p>
        </the-modal-box>

        <the-modal-box v-model="isConfirmChangeSiteModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmChangeSite" @cancel="cancelChangeSite">
            <p>{{ __('Are you sure you want to change the site? It will remove selected nomenclature.') }}</p>
        </the-modal-box>

        <the-modal-box v-model="isGenerationModalActive" :title="__('Please wait')" :has-control="false">
            <p>{{
                    __('Your :resource is generated, please do not refresh the page', {resource: __('Feed').toLowerCase()})
                }}</p>
            <p class="flex justify-center pt-2">
                <!-- By Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL -->
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="15" viewBox="0 0 120 30" fill="#3b82f6">
                    <circle cx="15" cy="15" r="15">
                        <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                        <animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="105" cy="15" r="15">
                        <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                </svg>
            </p>
        </the-modal-box>

        <card-component :title="__('Feed Settings')" :icon="mdiListStatus" @submit.prevent="submit" form>
            <div class="md:grid md:grid-cols-2 md:gap-2">
                <div>
                    <field :label="__('Author')" :error="errors.user_id ?? ''">
                        <control :disabled="!isPMUser" :icon="mdiBookAccount" :options="userOptions" v-model="form.user_id"
                                 type="select-search"
                                 placeholder="Автор"
                                 :required="true"/>
                    </field>
                    <field :label="__('Feed name')" :error="errors.name ?? ''">
                        <control :icon="mdiFormatColorText" v-model="form.name" :required="true"/>
                    </field>
                    <field :label="__('Site')" :error="errors.site ?? ''">
                        <control :icon="mdiLink" :placeholder="__('Site')" v-model="formSite" :options="siteOptions"
                                 :required="true"/>
                    </field>
                    <field :label="__('Feed type')" :error="errors.feed_type ?? ''">
                        <control :icon="mdiPuzzleOutline" :options="feedTypesOptions" v-model="form.feed_type"
                                 :required="true"/>
                    </field>
                          <field v-if="form.feed_type !== null && form.feed_type.id === 'xml'" :label="__('Update interval')"
                                    :error="errors.update_interval_custom ?? ''">
                                <control :icon="mdiAvTimer" :options="updateIntervalCustomOptions" v-model="form.update_interval_custom"
                                            :required="true"/>
                          </field>
                          <field v-else :label="__('Update interval')"
                           :help="__('0-8 times a day, starting at 6 o\'clock in the morning')"
                           :error="errors.update_interval ?? ''">
                        <control :icon="mdiAvTimer" :options="updateIntervalOptions" v-model="form.update_interval"
                                 :required="true"/>
                    </field>
                    <field v-if="form.feed_type !== null && form.feed_type.id === 'google_products'" class="mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field v-if="form.feed_type !== null && form.feed_type.id === 'google_products'" class="mb-0" :label="__('Exclude products')">
                        <check-radio-picker
                            class="checkbox-block"
                            name="excluded_products"
                            type="checkbox mr-0"
                            v-model="form.excluded_products"
                            :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                        />
                    </field>
                </div>
                <div>
                    <catalog-selector v-model="form.nomenclatures" :site="formSite"/>
                    <stores-selector v-model="form.stores" :site="formSite"/>
                    <field :label="__('Feed language')" :error="errors.feed_language ?? ''">
                        <control :icon="mdiTranslate" :options="languageOptionData" v-model="form.feed_language"
                                 :required="true" :disabled="languageOptionsDisabled"/>
                    </field>
                </div>
                <div v-if="form.feed_type !== null && form.feed_type.id === 'facebook_catalog'">
                    <field :label="__('Store title')" :error="errors.title ?? ''">
                        <control :icon="mdiFormatColorText" v-model="form.title" :disabled="siteMetaDisabled"/>
                    </field>
                    <field :label="__('Store description')" :help="__('Max 9999 characters')">
                        <control :icon="mdiText" type="textarea" v-model="form.description"
                                 :disabled="siteMetaDisabled"/>
                    </field>
                </div>
                <div v-if="form.feed_type !== null && form.feed_type.id === 'facebook_catalog'">
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="usd_currency"
                            type="switch"
                            v-model="form.usd_currency"
                            :options="{ usd_currency: __('USD Currency') }"
                        ></check-radio-picker>
                    </field>
                    <field class="mb-0 mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="sample-switch"
                            type="switch"
                            v-model="form.exchange_changes"
                            :options="{ exchange_changes: __('Update when the exchange rate changes') }"
                        />
                    </field>
                </div>
                <div v-if="form.feed_type !== null && form.feed_type.id === 'facebook_catalog'">
                    <field class="mb-0" :label="__('Exclude products')">
                        <check-radio-picker
                            class="checkbox-block"
                            name="excluded_products"
                            type="checkbox mr-0"
                            v-model="form.excluded_products"
                            :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                        />
                    </field>
                </div>
            </div>

        </card-component>

        <card-component v-if="form.feed_type !== null && form.feed_type.id === 'xml'"
                        :title="__('Feed Fields Settings')"
                        :icon="mdiViewColumnOutline">
            <div class="md:grid md:grid-cols-2 md:gap-2 mt-4">
                <field :error="errors.feed_fields ?? ''" class="col-span-1">
                    <check-radio-picker class="checkbox-block" type="checkbox mr-0"
                                        name="sample-checkbox"
                                        v-model="form.feed_fields"
                                        :options="feedFieldsOptions"
                    />
                </field>
                <card-component class="col-span-1">
                    <field class="max-w-sm mb-2" :label="__('Price additional image')"
                           :error="errors.additional_image ?? ''">
                        <control :icon="mdiImage" :options="additionalImageOptions" v-model="form.additional_image"
                                 :required="true"/>
                    </field>
                    <field class="mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field class="mb-0" :label="__('Exclude products')">
                        <check-radio-picker
                            class="checkbox-block"
                            name="excluded_products"
                            type="checkbox mr-0"
                            v-model="form.excluded_products"
                            :options="{ discontinued: __('Discontinued'), not_for_sale: __('Removed from sale'), pre_order: __('On order') }"
                        />
                    </field>
                </card-component>
            </div>
        </card-component>

        <card-component v-if="form.feed_type !== null && form.feed_type.id === 'facebook_catalog'"
                        :title="__('Price Price Settings')"
                        :icon="mdiCurrencyUsd"
                        class="col-span-2">
            <field :error="errors.price_price ?? ''">
                <prices-picker class="checkbox-grid" type="radio"
                                name="price-radio"
                                v-model="form.price_price"
                                :options="pricePricesOptions"
                                :priority="form.price_priority"
                />
            </field>
        </card-component>

        <card-component :title="__('Download link')" :icon="mdiLink">
            <span class="text-blue-700 break-words">
                <a :href="route('feed-configs.download', feedConfig.hash)" target="_blank">
                    {{ route('feed-configs.download', feedConfig.hash) }}
                </a>
            </span>
        </card-component>

        <jb-buttons class="md:justify-end ml-3 md:ml-0" mb="">
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" type="button"
                       color="success" :label="__('Save :resource', {resource: __('Feed').toLowerCase()})"
                       @click.prevent="submit"/>
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" type="button"
                       @click="downloadFeed"
                       target="_blank"
                       color="info" :label="__('Download :resource', {resource: __('Feed').toLowerCase()})"/>
            <jb-button type="button" color="warning" outline :label="__('Cancel')"
                       @click.prevent="cancel"/>
            <jb-button type="button" color="danger" outline
                       :label="__('Delete :resource', {resource: __('Feed').toLowerCase()})"
                       @click.prevent="isConfirmModalActive = true"/>
        </jb-buttons>

    </BreezeDashboardLayout>

    <iframe id="download" style="display:none;"></iframe>
</template>

<style>
</style>

<script>
import BreezeDashboardLayout from '@/Layouts/Dashboard'
import CardComponent from '@/Components/Dashboard/CardComponent'
import TheModalBox from '@/Components/Dashboard/TheModalBox'
import Notification from '@/Components/Dashboard/Notification'
import {Head, usePage} from '@inertiajs/inertia-vue3';
import {ref, computed} from 'vue'
import {
    mdiBookAccount,
    mdiListStatus, mdiAccount, mdiEmail, mdiLink, mdiOpenInNew, mdiPuzzleOutline, mdiAvTimer, mdiTranslate,
    mdiPlusBox, mdiCloseBoxMultiple, mdiCurrencyUsd, mdiViewColumnOutline, mdiFormatColorText, mdiText, mdiImage
} from '@mdi/js'
import Field from '@/Components/Dashboard/Field'
import Control from '@/Components/Dashboard/Control'
import CheckRadioPicker from '@/Components/Dashboard/CheckRadioPicker'
import PricesPicker from '@/Components/Dashboard/PricesPicker'
import JbButton from '@/Components/Dashboard/JbButton'
import JbButtons from '@/Components/Dashboard/JbButtons'
import CatalogSelector from "@/Components/Dashboard/CatalogSelector"
import StoresSelector from "@/Components/Dashboard/StoresSelector"

export default {
    components: {
        CatalogSelector,
        StoresSelector,
        BreezeDashboardLayout,
        Head,
        TheModalBox,
        Notification,
        CardComponent,
        Field,
        Control,
        CheckRadioPicker,
        PricesPicker,
        JbButton,
        JbButtons
    },
    props: {
        success: String,
        errors: Object,
        feedConfig: Object,
        pricePricesOptions: Object,
        feedTypesOptions: Array,
        updateIntervalOptions: Array,
        updateIntervalCustomOptions: Array,
        feedFieldsOptions: Object,
        languageOptions: Array,
        userOptions: Array,
        siteOptions: Array,
        additionalImageOptions: Array,
        isPMUser: Boolean,
    },
    watch: {
        formSite(newVal, oldVal) {
            if (this.formSiteOldValue === null) {
                if (this.form.nomenclatures.length > 0 || this.form.name !== '' || this.form.email !== '' || this.form.stores.length > 0) {
                    this.isConfirmChangeSiteModalActive = true
                    this.formSiteOldValue = oldVal
                } else {
                    this.form.site = this.formSite
                    this.setLanguageOptions()
                    this.setSiteMeta()
                }
            } else {
                this.formSiteOldValue = null
            }
        },
        "form.nomenclatures": {
            handler() {
                this.autoSave()
            },
            deep: true
        },
        "form.feed_fields": {
            handler() {
                this.autoSave()
            },
            deep: true
        },
        "form.stores": {
            handler(newVAl, oldVal) {
                this.availableProductsToggle(newVAl.length > 0)
                this.autoSave()
            },
            deep: true
        },
        "form.user_id"() {
            this.autoSave()
        },
        "form.name"() {
            this.autoSave()
        },
        "form.update_interval"() {
            this.autoSave()
        },
        "form.update_interval_custom"() {
            this.autoSave()
        },
        "form.additional_image"() {
            this.autoSave()
        },
        "form.usd_currency"() {
            this.autoSave()
        },
        "form.exchange_changes"() {
            this.autoSave()
        },
        "form.price_price"() {
            this.autoSave()
        },
        "form.only_available_products"() {
            this.autoSave()
        },
        "form.excluded_products": {
            handler() {
                this.autoSave()
            },
            deep: true
        },
        "form.feed_type": {
            handler(newVAl, oldVal) {
                this.setSiteMeta()
                this.autoSave()
            },
            deep: true
        },
        "form.feed_language": {
            handler(newVAl, oldVal) {
                this.setSiteMeta().then((response) => {
                    this.autoSave()
                });
            },
            deep: true
        }
    },
    data(props) {
        return {
            form: this.$inertia.form({
                user_id: props.userOptions.find(item => item.id === props.feedConfig.user_id),
                site: props.siteOptions.find(item => item.id === props.feedConfig.site_id),
                name: props.feedConfig.name ?? '',
                title: props.feedConfig.title,
                description: props.feedConfig.description,
                nomenclatures: props.feedConfig.nomenclatures.map(item => {
                    const data = {
                        id: item.api_source_id,
                        catalog_id: item.catalog_id,
                        subject_id: item.subject_id ?? null,
                        name: item.name,
                        type: item.type
                    }

                    if (data.type === 'catalog') {
                        data.id = data.catalog_id
                    }

                    return data
                }),
                stores: props.feedConfig.stores.map(item => {
                    return {
                        id: item.api_source_id,
                        name: item.name,
                        type: item.type
                    }
                }),
                feed_type: props.feedTypesOptions.find(item => item.id === props.feedConfig.feed_type),
                update_interval: props.updateIntervalOptions.find(item => item.id === props.feedConfig.update_interval),
                update_interval_custom: props.updateIntervalCustomOptions.find(item => item.id === props.feedConfig.update_interval_custom),
                feed_fields: props.feedConfig.feed_fields.map(item => item.id.toString()),
                additional_image: props.additionalImageOptions.find(item => item.id == props.feedConfig.additional_image) ?? props.additionalImageOptions[0],
                feed_language: props.languageOptions.find(item => item.id === props.feedConfig.feed_language),
                usd_currency: props.feedConfig.usd_currency ? ['usd_currency'] : [],
                exchange_changes: props.feedConfig.exchange_changes ? ['exchange_changes'] : [],
                price_price: props.feedConfig.price_price.api_source_id,
                only_available_products: props.feedConfig.only_available_products == 1,
                excluded_products: props.feedConfig.excluded_products ?? [],
                price_priority: 0,
                dot_not_redirect: false
            }),
            formSite: props.siteOptions.find(item => item.id === props.feedConfig.site_id),
            formSiteOldValue: null,
            isLoading: false,
            languageOptionsDisabled: false,
            languageOptionData: props.languageOptions,
            selectedUser: props.userOptions.find(item => item.id === props.feedConfig.user_id),
            availableProductsReadonly: props.feedConfig.stores.length > 0,
            siteMetaDisabled: false
        }
    },
    mounted() {
        document.getElementById('download').removeAttribute('src');
    },
    setup(props) {
        const isConfirmModalActive = ref(false)
        const isGenerationModalActive = ref(false)
        const isConfirmChangeSiteModalActive = ref(false)
        const user = computed(() => usePage().props.value.auth.user)

        return {
            user,
            mdiBookAccount,
            mdiListStatus,
            mdiAccount,
            mdiLink,
            mdiOpenInNew,
            mdiEmail,
            mdiPuzzleOutline,
            mdiAvTimer,
            mdiTranslate,
            mdiPlusBox,
            mdiCloseBoxMultiple,
            mdiCurrencyUsd,
            mdiViewColumnOutline,
            mdiFormatColorText,
            mdiText,
            mdiImage,
            isConfirmModalActive,
            isConfirmChangeSiteModalActive,
            isGenerationModalActive
        }
    },
    methods: {
        availableProductsToggle(setAndBlock = true) {
            if (setAndBlock) {
                this.form.only_available_products = true
                this.availableProductsReadonly = true
            } else {
                this.form.only_available_products = false
                this.availableProductsReadonly = false
            }
        },
        downloadFeed() {
            const self = this
            let needModal = true

            setTimeout(() => {
                if (needModal) {
                    self.isGenerationModalActive = true
                }
            }, 500)

            axios.post(this.route('api.v1.feed-configs.async-download'), {
                'hash': this.feedConfig.hash
            }).then(response => {
                needModal = false
                setTimeout(() => {
                    self.isGenerationModalActive = false
                }, 1000)
                document.getElementById('download').src = self.route('feed-configs.download', self.feedConfig.hash);
            }).then(error => {
                self.isGenerationModalActive = false
            });
        },
        getLanguageCodeById(id) {
            let code = 'en'
            if (id == 5) {
                code = 'ru'
            } else if (id == 6) {
                code = 'uk'
            } else if (id == 3) {
                code = 'es'
            }
            return code
        },
        autoSave() {

            const self = this
            self.form.dot_not_redirect = true
            self.form.processing = true
            self.$emit('errors', {})
            axios.put(this.route('feed-configs.update', this.feedConfig.id), this.form).then(res => {
                self.clearAfterAutoSave()
            }).catch(error => {
                const data = error.response.data
                if (typeof data.errors !== "undefined") {
                    self.$emit('errors', data.errors)
                }
                self.clearAfterAutoSave()
            })

            // notif
        },
        clearAfterAutoSave() {
            const self = this
            setTimeout(() => {
                self.form.dot_not_redirect = false
                self.form.processing = false
            }, 500)
        },
        submit() {
            this.form.put(this.route('feed-configs.update', this.feedConfig.id))
        },
        cancel() {
            this.$inertia.visit(this.route('feed-configs.index'))
        },
        share() {
            if (navigator.canShare && navigator.canShare({files: filesArray})) {
                navigator.share({
                    files: filesArray,
                    title: 'Pictures',
                    text: 'Our Pictures.',
                })
                    .then(() => console.log('Share was successful.'))
                    .catch((error) => console.log('Sharing failed', error));
            } else {
                console.log(`Your system doesn't support sharing files.`);
            }
        },
        confirmDeletion() {
            this.$inertia.form().delete(this.route('feed-configs.destroy', this.feedConfig.id))
        },
        cancelChangeSite() {
            this.formSite = this.formSiteOldValue;
        },
        confirmChangeSite() {
            this.formSiteOldValue = null
            this.form.site = this.formSite
            this.setLanguageOptions()
            this.setSiteMeta()
            this.form.nomenclatures = []
            this.form.stores = []
        },
        setSiteMeta() {
            const self = this
            self.siteMetaDisabled = true

            return new Promise((resolve, reject) => {
                this.apiRequest('api.v1.site-meta.get', {
                    'siteId': this.form.site.api_source_id,
                    'languageCode': this.form.feed_language.id
                }).then(response => {
                    self.siteMetaDisabled = false
                    if (typeof response.data === "undefined") {
                        return
                    }

                    if (typeof response.data.title) {
                        self.form.title = response.data.title
                    }

                    if (typeof response.data.description) {
                        self.form.description = response.data.description
                    }
                    resolve(response)
                }).catch(error => {
                    self.siteMetaDisabled = false
                    reject(error)
                })
            });
        },
        setLanguageOptions() {
            const self = this
            self.languageOptionsDisabled = true
            this.apiRequest('api.v1.languages.index', {'siteId': this.form.site.api_source_id}).then(response => {
                self.languageOptionsDisabled = false
                if (typeof response.data === "undefined") {
                    return
                }

                self.languageOptionData = response.data.map(language => {
                    return {'id': language.code, 'label': language.name}
                })

                self.$emit('update:languageOptions', self.languageOptionData)

                self.form.feed_language = self.languageOptionData[0]

            }).catch(error => {
                self.languageOptionsDisabled = false
            })
        },
        apiRequest(route, data) {
            const self = this
            return new Promise((resolve, reject) => {
                axios.post(this.route(route), data).then(response => {
                    resolve(response)
                }).catch(error => {
                    console.error(error)
                    reject(error)
                });
            });
        },
    }
}
</script>
